'use client';

import { CurrencyCode, CurrencySymbolAdapter } from 'geo-lookup';
import { createContext, JSX, PropsWithChildren, useCallback, useContext, useState } from 'react';
import { Plan } from 'subscription-service-client';

export type CheckoutContext = {
    error?: string | undefined;
    setError: (error: string) => void;

    currency: string;
    setCurrency: (newCurrency: string) => void;
    currencySymbol: string;
    countryCode: string;
    setCountryCode: (newCountryCode: string) => void;
    postalCode?: string | undefined;
    setPostalCode: (newPostalCode?: string) => void;
    promoCode?: string | undefined;
    setPromoCode: (newPromoCode?: string) => void;
    isPromoFromUrl: boolean;

    planCode: string;
    setPlanCode: (newPlanCode: string) => void;
    monthlyPlan: Plan | null;
    yearlyPlan: Plan | null;
    hidePaypal: boolean;
};
const context = createContext<CheckoutContext>({
    setError: () => {},

    currency: 'USD',
    setCurrency: () => {},
    currencySymbol: '$',
    countryCode: 'US',
    setCountryCode: () => {},
    setPostalCode: () => {},
    setPromoCode: () => {},
    isPromoFromUrl: false,
    planCode: '',
    setPlanCode: () => {},
    monthlyPlan: null,
    yearlyPlan: null,
    hidePaypal: false,
});

export type CheckoutContextProps = {
    detectedCurrencyCode: string;
    detectedCurrencySymbol: string;
    detectedCountry: string;
    initialPlanCode: string;
    initialPromoCode?: string | null;
    monthlyPlan: Plan | null;
    yearlyPlan: Plan | null;
    hidePaypal: boolean;
};

export function CheckoutContextProvider({
    detectedCurrencyCode,
    detectedCurrencySymbol,
    detectedCountry,
    initialPlanCode,
    initialPromoCode,
    monthlyPlan,
    yearlyPlan,
    hidePaypal,
    children,
}: PropsWithChildren<CheckoutContextProps>): JSX.Element {
    const [error, setError] = useState<string>();
    const [countryCode, setCountryCode] = useState<string>(detectedCountry);
    const [currencyCode, setCurrencyCode] = useState<string>(detectedCurrencyCode);
    const [currencySymbol, setCurrencySymbol] = useState<string>(detectedCurrencySymbol);
    const [postalCode, setPostalCode] = useState<string>();
    const [promoCode, setPromoCode] = useState<string | undefined>(initialPromoCode ?? undefined);
    const [planCode, setPlanCode] = useState<string>(initialPlanCode);

    // Track if promo code came from URL
    const isPromoFromUrl = Boolean(initialPromoCode);

    const setCurrencyCodeCallback = useCallback(
        (newCurrencyCode: string) => {
            const newCurrencySymbol = CurrencySymbolAdapter.fromCurrencyCode(newCurrencyCode as CurrencyCode);
            setCurrencyCode(newCurrencyCode);
            setCurrencySymbol(newCurrencySymbol);
        },
        [setCurrencySymbol, setCurrencyCode],
    );

    return (
        <context.Provider
            value={{
                error,
                setError,

                currency: currencyCode,
                setCurrency: setCurrencyCodeCallback,
                currencySymbol,
                countryCode,
                setCountryCode,
                postalCode,
                setPostalCode,
                promoCode,
                setPromoCode,
                isPromoFromUrl,
                planCode,
                setPlanCode,
                monthlyPlan,
                yearlyPlan,
                hidePaypal,
            }}
        >
            {children}
        </context.Provider>
    );
}

export const useCheckoutContext = (): CheckoutContext => useContext(context);
